import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, Nav, NavItem, NavLink, TabContent, TabPane, Badge, Alert, InputGroup, InputGroupText } from 'reactstrap';

import axios from '../../../store/axios';
import { updatePlan } from '../../../store/Admin';
import PlanSelect from '../../../components/PlanSelect/PlanSelect';
import { PLAN_TYPE_PILL_LABELS, INTERVAL_PILL_LABELS } from './planLabels';

const PlanEditModal = ({ isOpen, toggle, plan, onUpdate, sourcePlan = null, defaultsPlan = null, plansList = [], isAgencyView = false, workspaceId = null, stripeConfigured = false }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [createdPlan, setCreatedPlan] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showDiscount, setShowDiscount] = useState(false);
  const [templatePlanId, setTemplatePlanId] = useState('');
  // Admin create mode: no plan prop — creates a custom done-for-you plan (sourcePlan prefills for Duplicate)
  const isCustomCreate = !plan && !isAgencyView;
  // Reason: number fields hold '' (not 0) so empty inputs show their placeholder; buildSubmitData
  // casts back to numbers before anything is sent to the server
  const [formData, setFormData] = useState({
    displayName: '',
    description: '',
    customerEmail: '',
    price: '',
    type: 'subscription',
    interval: 'month',
    isActive: true,
    currency: 'USD',
    monthlyCredits: '',
    planType: 'business',
    popularPlan: false,
    btnText: 'Get Started',
    features: {
      subWorkspaces: '',
      contacts: '',
      users: '',
      emails: '',
      callingMinutes: '',
      agentExecutions: '',
      preBuiltAgents: '',
      additionalFeatures: [],
      supportType: ''
    },
    featuresWithIcons: [],
    creditsConfig: {
      credits: '',
      bonusCredits: ''
    },
    metadata: {
      pricePerCredit: '',
      discountPercent: '',
      isCustom: false,
      minimumCredits: '',
      order: ''
    }
  });

  useEffect(() => {
    const sourceData = plan || sourcePlan;
    if (sourceData) {
      setFormData({
        price: sourceData.price || '',
        displayName: sourceData.displayName || '',
        description: sourceData.description || '',
        customerEmail: plan ? (plan.customerEmail || '') : '', // Duplicate targets a new customer — don't copy the email
        type: sourceData.type || 'subscription',
        interval: sourceData.interval || 'month',
        currency: sourceData.currency || 'USD',
        planType: sourceData.planType || 'business',
        popularPlan: sourceData.popularPlan || false,
        btnText: sourceData.btnText || 'Get Started',
        monthlyCredits: sourceData.monthlyCredits || '',
        isActive: sourceData.isActive !== undefined ? sourceData.isActive : true,
        features: {
          contacts: sourceData.features?.contacts || '',
          users: sourceData.features?.users || '',
          emails: sourceData.features?.emails || '',
          subWorkspaces: sourceData.features?.subWorkspaces || '',
          callingMinutes: sourceData.features?.callingMinutes || '',
          agentExecutions: sourceData.features?.agentExecutions || '',
          preBuiltAgents: sourceData.features?.preBuiltAgents || '',
          additionalFeatures: sourceData.features?.additionalFeatures || [],
          supportType: sourceData.features?.supportType || ''
        },
        featuresWithIcons: (sourceData.featuresWithIcons || []).map(feature => ({ ...feature })),
        creditsConfig: {
          credits: sourceData.creditsConfig?.credits || '',
          bonusCredits: sourceData.creditsConfig?.bonusCredits || ''
        },
        metadata: {
          pricePerCredit: sourceData.metadata?.pricePerCredit || '',
          discountPercent: sourceData.metadata?.discountPercent || '',
          isCustom: sourceData.metadata?.isCustom || false,
          minimumCredits: sourceData.metadata?.minimumCredits || '',
          order: sourceData.metadata?.order || ''
        }
      });
    }
    // Reason: fresh custom plans start from the free Starter plan's limits so the admin only adjusts price and extras
    if (!sourceData && isCustomCreate && defaultsPlan) {
      setFormData(prev => ({
        ...prev,
        monthlyCredits: defaultsPlan.monthlyCredits || '',
        metadata: { ...prev.metadata, pricePerCredit: defaultsPlan.metadata?.pricePerCredit || '' },
        features: {
          ...prev.features,
          contacts: defaultsPlan.features?.contacts || '',
          users: defaultsPlan.features?.users || '',
          emails: defaultsPlan.features?.emails || '',
          callingMinutes: defaultsPlan.features?.callingMinutes || '',
          agentExecutions: defaultsPlan.features?.agentExecutions || '',
          preBuiltAgents: defaultsPlan.features?.preBuiltAgents || '',
          supportType: defaultsPlan.features?.supportType || ''
        }
      }));
    }
    // Reason: custom plans are always monthly doneForYou subscriptions marked isCustom
    if (isCustomCreate) {
      setFormData(prev => ({ ...prev, type: 'subscription', interval: 'month', planType: 'doneForYou', isActive: true, btnText: 'Pay with Stripe', metadata: { ...prev.metadata, isCustom: true } }));
      setShowDiscount(Number(sourceData?.metadata?.discountPercent) > 0);
    }
  }, [plan, sourcePlan, defaultsPlan]);

  const handleInputChange = (e, section = null) => {
    const { name, value, type, checked } = e.target;
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    // Reason: keep number fields as strings while typing (parseFloat re-rendering glues input onto the
    // initial 0 → "01221"); leading zeros stripped, mongoose/validators cast on submit
    let actualValue = type === 'checkbox' ? checked :
                       type === 'number' ? value.replace(/^0+(?=\d)/, '') : value;
    // Reason: percentage is hard-capped live — out-of-range values can't even be typed
    if (name === 'discountPercent' && actualValue !== '') actualValue = String(Math.min(100, Math.max(0, Number(actualValue) || 0)));

    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [name]: actualValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: actualValue
      }));
    }
  };

  const handleAdditionalFeatureChange = (index, value) => {
    const newFeatures = [...formData.features.additionalFeatures];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        additionalFeatures: newFeatures
      }
    }));
  };

  const addAdditionalFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        additionalFeatures: [...prev.features.additionalFeatures, '']
      }
    }));
  };

  const removeAdditionalFeature = (index) => {
    const newFeatures = formData.features.additionalFeatures.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        additionalFeatures: newFeatures
      }
    }));
  };

  // Card feature lines (featuresWithIcons) — edit text only, keep each line's stored icon untouched
  const handleCardFeatureChange = (index, text) => setFormData(prev => ({ ...prev, featuresWithIcons: prev.featuresWithIcons.map((feature, i) => i === index ? { ...feature, text } : feature) }));

  const addCardFeature = () => setFormData(prev => ({ ...prev, featuresWithIcons: [...prev.featuresWithIcons, { text: '', iconSvg: '<i class="fa fa-check"></i>' }] }));

  const removeCardFeature = (index) => setFormData(prev => ({ ...prev, featuresWithIcons: prev.featuresWithIcons.filter((_, i) => i !== index) }));

  // Copies limits/pricing from a chosen existing plan; name, customer email and discount stay untouched.
  // Reason: seeded plans carry their marketing lines ("20 Managed Inboxes", "5,000 Leads Sourced/month",
  // CRM plan, warmup, channels...) only in featuresWithIcons — surface those as Additional Features,
  // excluding lines the server re-derives from the numeric fields (to avoid duplicates on the card).
  const handleApplyTemplate = (planId) => {
    setTemplatePlanId(planId);
    const source = plansList.find(p => p._id === planId);
    if (!source) return;
    const templateLines = (source.featuresWithIcons || [])
      .map(feature => feature.text)
      .filter(Boolean)
      .filter(text => !NUMERIC_FEATURE_LINE.test(text));
    setFormData(prev => ({
      ...prev,
      description: source.description || prev.description,
      price: source.price || '',
      monthlyCredits: source.monthlyCredits || '',
      metadata: { ...prev.metadata, pricePerCredit: source.metadata?.pricePerCredit || '' },
      features: {
        ...prev.features,
        contacts: source.features?.contacts || '',
        users: source.features?.users || '',
        emails: source.features?.emails || '',
        callingMinutes: source.features?.callingMinutes || '',
        agentExecutions: source.features?.agentExecutions || '',
        preBuiltAgents: source.features?.preBuiltAgents || '',
        additionalFeatures: templateLines.length ? templateLines : (source.features?.additionalFeatures?.length ? [...source.features.additionalFeatures] : prev.features.additionalFeatures),
        supportType: source.features?.supportType || ''
      }
    }));
  };

  // Unchecking the discount toggle clears any entered percentage so the plan is created without a coupon
  const handleDiscountToggle = (e) => {
    const checked = e.target.checked;
    setShowDiscount(checked);
    if (!checked) {
      setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, discountPercent: '' } }));
      setFormErrors(prev => ({ ...prev, discountPercent: '' }));
    }
  };

  const validateCustomPlan = () => {
    const errors = {};
    if (!formData.displayName || !formData.displayName.trim()) errors.displayName = 'Display name is required';
    if (!(Number(formData.price) > 0)) errors.price = 'Price must be greater than 0';
    const discount = Number(formData.metadata.discountPercent) || 0;
    if (discount < 0 || discount > 100) errors.discountPercent = 'Discount must be between 0 and 100';
    if (formData.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) errors.customerEmail = 'Enter a valid email address';
    if (Number(formData.monthlyCredits) < 0) errors.monthlyCredits = 'Credits cannot be negative';
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setActiveTab('basic'); // All validated fields live on the Basic Info tab
      return false;
    }
    return true;
  };

  // Reason: form state holds '' for empty number fields (placeholder-friendly) — cast everything
  // back to real numbers before sending, or Mongoose number casts would fail on ''
  const buildSubmitData = () => {
    const toNumber = (value) => value === '' || value === null || isNaN(Number(value)) ? 0 : Number(value);
    return {
      ...formData,
      price: toNumber(formData.price),
      monthlyCredits: toNumber(formData.monthlyCredits),
      featuresWithIcons: formData.featuresWithIcons.filter(feature => feature.text && feature.text.trim()),
      features: {
        ...formData.features,
        subWorkspaces: toNumber(formData.features.subWorkspaces),
        contacts: toNumber(formData.features.contacts),
        users: toNumber(formData.features.users),
        emails: toNumber(formData.features.emails),
        callingMinutes: toNumber(formData.features.callingMinutes),
        agentExecutions: toNumber(formData.features.agentExecutions),
        preBuiltAgents: toNumber(formData.features.preBuiltAgents)
      },
      creditsConfig: {
        credits: toNumber(formData.creditsConfig.credits),
        bonusCredits: toNumber(formData.creditsConfig.bonusCredits)
      },
      metadata: {
        ...formData.metadata,
        pricePerCredit: toNumber(formData.metadata.pricePerCredit),
        discountPercent: toNumber(formData.metadata.discountPercent),
        minimumCredits: toNumber(formData.metadata.minimumCredits),
        order: toNumber(formData.metadata.order)
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCustomCreate && !validateCustomPlan()) return;
    setLoading(true);

    try {
      const submitData = buildSubmitData();
      if (isAgencyView) {
        // Handle agency plans
        if (plan?.isNew) {
          // Create new plan
          const response = await axios.post('/payment/plans/agency', {
            ...submitData,
            workspaceId: workspaceId,
            isDraft: !stripeConfigured, // Set as draft if Stripe not configured
            stripeRequired: !stripeConfigured
          });

          if (response.data.success) {
            toast.success('Plan created successfully');
            onUpdate();
          }
        } else {
          // Update existing plan
          const response = await axios.put(`/payment/plans/agency/${plan._id}`, submitData);

          if (response.data.success) {
            toast.success('Plan updated successfully');
            onUpdate();
          }
        }
      } else if (isCustomCreate) {
        // Create a custom done-for-you plan (Duplicate prefills the form; submit always creates a new plan)
        const payload = { displayName: submitData.displayName, description: submitData.description, price: submitData.price, monthlyCredits: submitData.monthlyCredits, customerEmail: submitData.customerEmail, discountPercent: submitData.metadata.discountPercent, features: submitData.features };
        const response = await axios.post('/admin/billing/plans', payload);
        if (response.data.code === 200) setCreatedPlan(response.data.body);
        else toast.error(response.data.message || 'Failed to create plan');
      } else {
        // Handle admin plans (existing logic)
        await dispatch(updatePlan({
          id: plan._id,
          ...submitData
        }));
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(error.response?.data?.message || 'Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = createdPlan ? `${window.location.origin}/plan/${createdPlan.shareToken}` : '';

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
      <ModalHeader toggle={toggle}>
        <h3>{isCustomCreate ? (sourcePlan ? `Duplicate Plan: ${sourcePlan.displayName || sourcePlan.name}` : 'Create Custom Plan') : plan?.isNew ? 'Create New Plan' : `Edit Plan: ${plan?.displayName || plan?.name}`}</h3>
        {isAgencyView && !stripeConfigured && (
          <Alert color="warning" className="mb-0 mt-2">
            <i className="fas fa-info-circle mr-1"></i>
            Plan will be saved as draft. Configure Stripe to activate it.
          </Alert>
        )}
      </ModalHeader>
      <Form className="mt-4" onSubmit={handleSubmit} noValidate>
        <ModalBody>
          {createdPlan ? (
            <div className="custom-plan-share-ready">
              <h5 className="custom-plan-share-title"><i className="fa fa-check-circle mr-2"></i>{createdPlan.displayName} is ready</h5>
              <p className="custom-plan-share-hint">Send this link to your customer — they can review the plan details and pay with Stripe.</p>
              <div className="d-flex custom-plan-share-link-row">
                <Input readOnly value={shareUrl} />
                <CopyToClipboard text={shareUrl} onCopy={() => toast.success('Link copied to clipboard!')}>
                  <button type="button" className="btn-style-new ml-2"><i className="fa fa-copy mr-1"></i>Copy</button>
                </CopyToClipboard>
              </div>
              {createdPlan.metadata?.discountPercent > 0 && (
                <Alert color="info" className="mt-3">
                  A {createdPlan.metadata.discountPercent}% first-month discount is applied automatically at checkout.
                </Alert>
              )}
            </div>
          ) : (
          <>
          {!isCustomCreate && plan && !plan.isNew && (
            <div className='custom-plan-fixed-meta custom-plan-header-tags'>
              <span className={`admin-plans-pill admin-plans-pill-${formData.planType || 'business'}`}>{PLAN_TYPE_PILL_LABELS[formData.planType] || formData.planType}</span>
              {formData.type === 'subscription' && formData.interval && <span className='admin-plans-pill admin-plans-pill-interval'>{INTERVAL_PILL_LABELS[formData.interval] || formData.interval}</span>}
              <span className='admin-plans-pill admin-plans-pill-interval'>{formData.type === 'credits' ? 'One-time credits' : formData.type === 'subscription' ? 'Subscription' : formData.type}</span>
              {formData.metadata.isCustom && formData.planType === 'doneForYou' && <span className='admin-plans-pill admin-plans-pill-popular'>Custom</span>}
            </div>
          )}
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === 'basic' ? 'active' : ''}
                onClick={() => setActiveTab('basic')}
                style={{ cursor: 'pointer' }}
              >
                Basic Info
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'features' ? 'active' : ''}
                onClick={() => setActiveTab('features')}
                style={{ cursor: 'pointer' }}
              >
                Features
              </NavLink>
            </NavItem>
            {!isCustomCreate && (
              <NavItem>
                <NavLink
                  className={activeTab === 'credits' ? 'active' : ''}
                  onClick={() => setActiveTab('credits')}
                  style={{ cursor: 'pointer' }}
                >
                  Credits & Pricing
                </NavLink>
              </NavItem>
            )}
            <NavItem>
              <NavLink
                className={activeTab === 'metadata' ? 'active' : ''}
                onClick={() => setActiveTab('metadata')}
                style={{ cursor: 'pointer' }}
              >
                Metadata
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-3">
            {/* Basic Info Tab */}
            <TabPane tabId="basic">
              {isCustomCreate && (
                <div className='custom-plan-fixed-meta'>
                  <span className='admin-plans-pill admin-plans-pill-doneForYou'>Done For You</span>
                  <span className='admin-plans-pill admin-plans-pill-interval'>Monthly subscription</span>
                  <span className='admin-plans-pill admin-plans-pill-interval'>USD</span>
                </div>
              )}
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Display Name</Label>
                    <Input
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      placeholder="Enter display name"
                      invalid={!!formErrors.displayName}
                      required
                    />
                    {formErrors.displayName && <div className='custom-plan-field-error'>{formErrors.displayName}</div>}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>{isCustomCreate ? 'Monthly Price' : 'Price'}</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText>$</InputGroupText>
                      <Input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        invalid={!!formErrors.price}
                        required
                      />
                    </InputGroup>
                    {formErrors.price && <div className='custom-plan-field-error'>{formErrors.price}</div>}
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter plan description"
                />
              </FormGroup>

              {isCustomCreate && (
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Customer Email (label only)</Label>
                      <Input
                        type="email"
                        name="customerEmail"
                        value={formData.customerEmail}
                        onChange={handleInputChange}
                        placeholder="customer@company.com"
                        invalid={!!formErrors.customerEmail}
                      />
                      {formErrors.customerEmail && <div className='custom-plan-field-error'>{formErrors.customerEmail}</div>}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className='custom-plan-discount-toggle'>
                      <Label check>
                        <Input type="checkbox" checked={showDiscount} onChange={handleDiscountToggle} />
                        {' '}Offer a first payment discount
                      </Label>
                    </FormGroup>
                    {showDiscount && (
                      <FormGroup>
                        <InputGroup className='custom-plan-input-group'>
                          <Input
                            type="number"
                            name="discountPercent"
                            value={formData.metadata.discountPercent}
                            onChange={(e) => handleInputChange(e, 'metadata')}
                            min="0"
                            max="100"
                            placeholder="e.g. 25"
                            invalid={!!formErrors.discountPercent}
                          />
                          <InputGroupText>%</InputGroupText>
                        </InputGroup>
                        {formErrors.discountPercent && <div className='custom-plan-field-error'>{formErrors.discountPercent}</div>}
                      </FormGroup>
                    )}
                  </Col>
                </Row>
              )}

              {!isCustomCreate && (
              <>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      disabled={isCustomCreate}
                    >
                      <option value="subscription">Subscription</option>
                      <option value="credits">Credits</option>
                      <option value="one-time">One-time</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Interval</Label>
                    <Input
                      type="select"
                      name="interval"
                      value={formData.interval}
                      onChange={handleInputChange}
                      disabled={formData.type !== 'subscription' || isCustomCreate}
                    >
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                      <option value="month">Monthly</option>
                      <option value="quarter">Quarterly</option>
                      <option value="year">Yearly</option>
                      <option value="lifetime">Lifetime</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Plan Type</Label>
                    <Input
                      type="select"
                      name="planType"
                      value={formData.planType}
                      onChange={handleInputChange}
                      disabled={isCustomCreate}
                    >
                      <option value="business">Business</option>
                      <option value="agency">Agency</option>
                      <option value="credits">Credits</option>
                      <option value="doneForYou">Done For You</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Button Text</Label>
                    <Input
                      name="btnText"
                      value={formData.btnText}
                      onChange={handleInputChange}
                      placeholder="e.g., Get Started"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Currency</Label>
                    <Input
                      type="select"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      {' '}Active
                    </Label>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        name="popularPlan"
                        checked={formData.popularPlan}
                        onChange={handleInputChange}
                      />
                      {' '}Popular Plan
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              </>
              )}
            </TabPane>

            {/* Features Tab */}
            <TabPane tabId="features">
              {isCustomCreate && plansList.length > 0 && (
                <FormGroup className='custom-plan-template-pick'>
                  <Label>Start from an existing plan</Label>
                  <PlanSelect
                    plans={plansList.filter(p => p.type === 'subscription')}
                    value={templatePlanId}
                    onChange={handleApplyTemplate}
                    valueKey="_id"
                    placeholder="Choose a plan to copy its limits & pricing…"
                  />
                </FormGroup>
              )}

              <Row>
                {formData.planType === 'agency' && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Sub-Workspaces</Label>
                      <InputGroup className='custom-plan-input-group'>
                        <InputGroupText><i className='fa fa-sitemap'></i></InputGroupText>
                        <Input type="number" name="subWorkspaces" value={formData.features.subWorkspaces} onChange={(e) => handleInputChange(e, 'features')} min="0" placeholder="0" />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                )}
                <Col md={6}>
                  <FormGroup>
                    <Label>Contacts</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-address-book'></i></InputGroupText>
                      <Input type="number" name="contacts" value={formData.features.contacts} onChange={(e) => handleInputChange(e, 'features')} min="0" placeholder="e.g. 10000" />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Users</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-users'></i></InputGroupText>
                      <Input type="number" name="users" value={formData.features.users} onChange={(e) => handleInputChange(e, 'features')} min="0" placeholder="e.g. 5" />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Emails</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-envelope'></i></InputGroupText>
                      <Input type="number" name="emails" value={formData.features.emails} onChange={(e) => handleInputChange(e, 'features')} min="0" placeholder="e.g. 250" />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Calling Minutes</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-phone'></i></InputGroupText>
                      <Input type="number" name="callingMinutes" value={formData.features.callingMinutes} onChange={(e) => handleInputChange(e, 'features')} min="0" placeholder="e.g. 50" />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Agent Executions</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-bolt'></i></InputGroupText>
                      <Input type="number" name="agentExecutions" value={formData.features.agentExecutions} onChange={(e) => handleInputChange(e, 'features')} min="0" placeholder="e.g. 10" />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Pre-Built Agents</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-magic'></i></InputGroupText>
                      <Input type="number" name="preBuiltAgents" value={formData.features.preBuiltAgents} onChange={(e) => handleInputChange(e, 'features')} min="0" placeholder="e.g. 3" />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Support Type</Label>
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-life-ring'></i></InputGroupText>
                      <Input name="supportType" value={formData.features.supportType} onChange={(e) => handleInputChange(e, 'features')} placeholder="e.g., Email, Priority, 24/7" />
                    </InputGroup>
                  </FormGroup>
                </Col>
                {isCustomCreate && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Monthly Credits</Label>
                      <InputGroup className='custom-plan-input-group'>
                        <InputGroupText><i className='fa fa-database'></i></InputGroupText>
                        <Input type="number" name="monthlyCredits" value={formData.monthlyCredits} onChange={handleInputChange} min="0" placeholder="e.g. 250" />
                      </InputGroup>
                      {formErrors.monthlyCredits && <div className='custom-plan-field-error'>{formErrors.monthlyCredits}</div>}
                    </FormGroup>
                  </Col>
                )}
              </Row>

              {!isCustomCreate && !plan?.isNew && (
                <FormGroup>
                  <Label>Plan Card Features</Label>
                  <p className='custom-plan-features-hint'>These lines appear on the plan card and pricing pages. Changing the limits above does not update them automatically.</p>
                  {formData.featuresWithIcons.map((feature, index) => (
                    <div key={index} className="d-flex mb-2">
                      <InputGroup className='custom-plan-input-group'>
                        <InputGroupText><i className='fa fa-check'></i></InputGroupText>
                        <Input
                          value={feature.text || ''}
                          onChange={(e) => handleCardFeatureChange(index, e.target.value)}
                          placeholder="e.g. 20 Managed Inboxes"
                        />
                      </InputGroup>
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        className="ml-2"
                        onClick={() => removeCardFeature(index)}
                      >
                        <i className="fa fa-trash"></i>
                      </Button>
                    </div>
                  ))}
                  <button type="button" className="custom-plan-add-feature-btn" onClick={addCardFeature}>
                    <i className="fa fa-plus mr-1"></i>Add Feature
                  </button>
                </FormGroup>
              )}

              {(isCustomCreate || formData.features.additionalFeatures.length > 0) && (
              <FormGroup>
                <Label>Additional Features</Label>
                {formData.features.additionalFeatures.map((feature, index) => (
                  <div key={index} className="d-flex mb-2">
                    <InputGroup className='custom-plan-input-group'>
                      <InputGroupText><i className='fa fa-check'></i></InputGroupText>
                      <Input
                        value={feature}
                        onChange={(e) => handleAdditionalFeatureChange(index, e.target.value)}
                        placeholder="e.g. GHL CRM integration included"
                      />
                    </InputGroup>
                    <Button
                      type="button"
                      color="danger"
                      size="sm"
                      className="ml-2"
                      onClick={() => removeAdditionalFeature(index)}
                    >
                      <i className="fa fa-trash"></i>
                    </Button>
                  </div>
                ))}
                <button type="button" className="custom-plan-add-feature-btn" onClick={addAdditionalFeature}>
                  <i className="fa fa-plus mr-1"></i>Add Feature
                </button>
              </FormGroup>
              )}
            </TabPane>

            {/* Credits & Pricing Tab */}
            <TabPane tabId="credits">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Monthly Credits</Label>
                    <Input
                      type="number"
                      name="monthlyCredits"
                      value={formData.monthlyCredits}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Price Per Credit</Label>
                    <Input
                      type="number"
                      name="pricePerCredit"
                      value={formData.metadata.pricePerCredit}
                      onChange={(e) => handleInputChange(e, 'metadata')}
                      min="0"
                      step="0.001"
                    />
                  </FormGroup>
                </Col>
              </Row>

              {formData.type === 'credits' && (
                <>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Credits</Label>
                        <Input
                          type="number"
                          name="credits"
                          value={formData.creditsConfig.credits}
                          onChange={(e) => handleInputChange(e, 'creditsConfig')}
                          min="0"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Bonus Credits</Label>
                        <Input
                          type="number"
                          name="bonusCredits"
                          value={formData.creditsConfig.bonusCredits}
                          onChange={(e) => handleInputChange(e, 'creditsConfig')}
                          min="0"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Minimum Credits</Label>
                        <Input
                          type="number"
                          name="minimumCredits"
                          value={formData.metadata.minimumCredits}
                          onChange={(e) => handleInputChange(e, 'metadata')}
                          min="0"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Discount Percent</Label>
                        <Input
                          type="number"
                          name="discountPercent"
                          value={formData.metadata.discountPercent}
                          onChange={(e) => handleInputChange(e, 'metadata')}
                          min="0"
                          max="100"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </>
              )}
            </TabPane>

            {/* Metadata Tab */}
            <TabPane tabId="metadata">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Display Order</Label>
                    <Input
                      type="number"
                      name="order"
                      value={formData.metadata.order}
                      onChange={(e) => handleInputChange(e, 'metadata')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup check className="custom-check-plan">
                    <Label check>
                      <Input
                        type="checkbox"
                        name="isCustom"
                        checked={formData.metadata.isCustom}
                        onChange={(e) => handleInputChange(e, 'metadata')}
                      />
                      {' '}Custom Plan
                    </Label>
                  </FormGroup>
                </Col>
              </Row>

              <Alert color="info" className="mt-3">
                <h6>Plan Information</h6>
                <div className="small">
                  <p className="mb-1"><strong>Plan ID:</strong> {plan?._id}</p>
                  <p className="mb-1"><strong>Name:</strong> {plan?.name}</p>
                  <p className="mb-1"><strong>Created:</strong> {plan?.createdAt ? new Date(plan.createdAt).toLocaleString() : 'N/A'}</p>
                  <p className="mb-1"><strong>Updated:</strong> {plan?.updatedAt ? new Date(plan.updatedAt).toLocaleString() : 'N/A'}</p>
                  {plan?.stripeProductId && (
                    <p className="mb-1"><strong>Stripe Product ID:</strong> {plan.stripeProductId}</p>
                  )}
                  {plan?.stripePriceId && (
                    <p className="mb-0"><strong>Stripe Price ID:</strong> {plan.stripePriceId}</p>
                  )}
                </div>
              </Alert>
            </TabPane>
          </TabContent>
          </>
          )}
        </ModalBody>
        <ModalFooter>
          {createdPlan ? (
            <button type="button" className="btn-style-new" onClick={() => onUpdate && onUpdate()}>
              Done
            </button>
          ) : (
            <>
              <button type="button" className="btn-style-border" color="secondary" onClick={toggle}>
                Cancel
              </button>
              <button className="btn-style-new" color="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {isCustomCreate ? 'Creating...' : 'Updating...'}
                  </>
                ) : (
                  <>
                    <i className="fa fa-save mr-2"></i>{isCustomCreate ? 'Create Plan & Get Link' : 'Update Plan'}
                  </>
                )}
              </button>
            </>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

// Lines starting with a quantity for a metric the numeric fields already cover ("Up to 15 users",
// "Upto 25,000 Emails", "1M CRM Contacts", "2500 credits/month included") — re-derived server-side
const NUMERIC_FEATURE_LINE = /^(up ?to\s+)?[\d,.]+[km]?\s+.*(users|contacts|emails|call minutes|agent executions|credits)/i;

export default PlanEditModal;